import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges, isDevMode } from '@angular/core';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TableModule } from 'primeng/table';
import { Address } from 'src/app/domain/Address';
import { ParcelShop } from 'src/app/domain/ParcelShop';
import { AddressService, UpdatedAutoCompleteAddressResponse } from 'src/app/services/address.service';
import { ClientService } from 'src/app/services/client.service';
import { CountryService } from 'src/app/services/country.service';
import { CustomerService } from 'src/app/services/customer.service';
import { ParcelShopService } from 'src/app/services/parcel-shop.service';
import { StepperService } from '../../stepper/stepper.service';
import { BehaviorSubject, catchError, forkJoin, of, Subscription } from 'rxjs';

@Component({
  selector: 'app-external-carrier-address',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, ButtonModule, TableModule, AutoCompleteModule, DropdownModule, CheckboxModule, RadioButtonModule, InputTextModule,],
  templateUrl: './external-carrier-address.componentV2.html',
  styleUrls: ['./external-carrier-address.component.scss'],
})
export class ExternalCarrierAddressComponent implements OnInit, OnChanges {
  @Input() shipment: any;
  @Input() addressForm!: FormGroup;
  @Input() services: any;
  @Input() multiSelectServices: boolean = false;
  @Input() contactPersons: any;

  countries: any;
  foundParcelShops: ParcelShop[] = [];
  foundCustomers: any[] = [];
  foundAddresses: any[] = [];
  loadingParcelShops: boolean = false;
  filteredContactPersons: any[] = [];
  addressSearchSubscription = new Subscription();

  constructor(
    private clientService: ClientService,
    private countryService: CountryService,
    private parcelShopService: ParcelShopService,
    private customerService: CustomerService,
    private addressService: AddressService,
    private stepperService: StepperService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    this.contactPersons = null;
    this.foundParcelShops = [];
  }

  ngOnInit(): void {
    this.fetchCountries();
    this.checkClientAddress();
    this.checkAddressForm();
  }

  externalServiceChange(event: any): void {
    if (event == "null") {
      this.stepperService.newShipment.externalCarrierServices = null;
      return;
    }
    // if((this.addressForm.get('externalCarrierServices')?.getRawValue() as {code:string}[]).map(x => x.code).length !> 0 )
    let selectedServices = this.addressForm.get('externalCarrierServices')?.value;
    if (this.stepperService.newShipment.externalCarrierServices == null)
      this.stepperService.newShipment.externalCarrierServices = new Array<{ code: string }>();
    this.stepperService.newShipment.externalCarrierServices = [selectedServices]

    let flex = Array.isArray(selectedServices) ? selectedServices.find((x: any) => x.code === 'flex' || x.code === 'A6') : selectedServices?.code === 'flex' || selectedServices?.code === 'A6' ? selectedServices : null;
    let noteControl = this.addressForm.get('note');

    if (!noteControl)
      return;
    if (flex)
      noteControl.setValidators([Validators.required, Validators.maxLength(40)]);
    else
      noteControl.setValidators([Validators.maxLength(40)]);
    noteControl.updateValueAndValidity();
  }

  onContactPersonSelect(event: any): void {
    this.addressForm.patchValue({ contactPhone: event.phone, contactEmail: event.email, contactPerson: { name: event.name }, });
    this.shipment.recipientAddress.contactPerson = event.name;
  }

  fetchCountries(): void {
    if (!this.countries) {
      this.countries = this.countryService.getEUCountriesJson();
    }
  }

  formattedParcelShopName(item: ParcelShop): string {
    if (item) {
      return `${item.number} : ${item.companyName} ${item.streetname} - ${item.cityName} ${item.zipCode} ${item.countryCodeISO3166A2}`;
    }

    return '';
  }

  formattedStreetName(item: Address | string): string {
    if (item && typeof item !== 'string' && item.street) {
      return `${item.street}`;
    }

    if (item) {
      return `${item}`;
    }

    return '';
  }

  searchAddress_Old(event: any): void {
    this.addressService.autoCompleteAddress(event.query).subscribe({
      next: (addressResult: UpdatedAutoCompleteAddressResponse) => {
        if (addressResult?.items) {
          this.foundAddresses = addressResult.items;
        } else {
          this.foundAddresses = [];
        }
      },
      error: () => {
        this.foundAddresses = [];
      },
    });
  }
  // this compines both backend autocomp and new KDS (replacment for DAWA)
  searchAddress(event: any) {
    const search = event.query?.trim();
    if (!search) {
      this.addressSearchSubscription?.unsubscribe();
      this.foundAddresses = [];
      return;
    }
    this.addressSearchSubscription?.unsubscribe();
    this.addressSearchSubscription = forkJoin({
      api: this.addressService.autoCompleteAddress(search) // backend autocomplte
        .pipe(catchError(error => {
          return of({ items: [] } as any);
        })
        ),

      kds: this.addressService.KdsAutocomplete(search)
    })
      .subscribe({
        next: ({ api, kds }) => {
          let addresses = [...kds, ...(api?.items ?? [])];
          let prioritized = [...addresses.filter(x => x.validatedBy === 'MongoDB'), ...addresses.filter(x => x.validatedBy !== 'MongoDB')];
          let seen = new Set<string>();

          this.foundAddresses = prioritized.filter(address => {
            let key = [address.street, address.houseNumber, address.postalCode].map(x => String(x ?? '').trim().toLowerCase()).join('|');

            if (seen.has(key)) return false;

            seen.add(key);
            return true;
          });
        },

        error: error => {
          console.error('Address search failed', error);
          this.foundAddresses = [];
        }
      });
  }

  filterContactPersons(event: any): void {
    if (!this.contactPersons) {
      this.filteredContactPersons = [];
      return;
    }

    const query = event.query?.toLowerCase() || '';

    this.filteredContactPersons = this.contactPersons.filter((contactPerson: any) =>
      contactPerson.name?.toLowerCase().startsWith(query)
    );
  }

  searchCustomer(event: any): void {
    this.customerService.searchCustomerFormatted(event.query).subscribe({
      next: (result: any) => {
        this.foundCustomers = result || [];
      },
      error: () => {
        this.foundCustomers = [];
      },
    });
  }

  setCustomer(cust: any): void {
    this.addressForm.patchValue({
      name: cust.name,
    });

    if (cust.contactPersons) {
      this.contactPersons = cust.contactPersons;
    }

    if (cust.contactPerson) {
      this.addressForm.patchValue({
        contactPerson: cust.contactPerson.name,
        contactPhone: cust.contactPerson.phone,
        contactEmail: cust.contactPerson.email,
      });

      this.shipment.recipientAddress.contactPerson = cust.contactPerson.name;
      this.shipment.recipientAddress.contactPhone = cust.contactPerson.phone;
      this.shipment.recipientAddress.contactEmail = cust.contactPerson.email;
    } else {
      this.addressForm.patchValue({
        contactPerson: null,
        contactPhone: null,
        contactEmail: null,
      });

      this.shipment.recipientAddress.contactPerson = null;
      this.shipment.recipientAddress.contactPhone = null;
      this.shipment.recipientAddress.contactEmail = null;
    }

    if (cust.address) {
      this.setAddress(cust.address);
    }
  }

  setAddress(event: any): void {
    this.loadingParcelShops = true;
    this.foundParcelShops = [];

    if (!event.countryCode) {
      const countryCodeControl = this.addressForm.get('countryCode');

      countryCodeControl?.setValidators([Validators.required]);
      countryCodeControl?.markAsDirty();
      countryCodeControl?.updateValueAndValidity();
    }

    this.addressForm.patchValue(
      {
        street: event.street,
        zipCode: event.zipCode,
        city: event.city,
        countryCode: event.countryCode,
      },
      { emitEvent: false }
    );

    this.shipment.recipientAddress.street = event.street;
    this.shipment.recipientAddress.zipCode = event.zipCode;
    this.shipment.recipientAddress.city = event.city;
    this.shipment.recipientAddress.countryCode = event.countryCode;
    this.shipment.recipientAddress.latitude = event.latitude;
    this.shipment.recipientAddress.longitude = event.longitude;
    this.shipment.recipientAddress.validatedBy = event.validatedBy;

    if (this.shipment.bookingType === 'PARCELSHOP') {
      this.fetchParcelShops();
    } else {
      this.loadingParcelShops = false;
    }

    this.foundAddresses = [];
  }

  fetchParcelShops(): void {
    if (this.shipment.carrier === 'GLS') {
      this.parcelShopService.getGLSParcelShopsByAddress(this.shipment.recipientAddress).subscribe({
        next: (result: any) => {
          this.foundParcelShops = result?.parcelshops || [];
          this.loadingParcelShops = false;
        },
        error: () => {
          this.foundParcelShops = [];
          this.loadingParcelShops = false;
        },
      });

      return;
    }

    if (this.shipment.carrier === 'Postnord') {
      this.parcelShopService.getPostnordParcelShopsByAddress(this.shipment.recipientAddress).subscribe({
        next: (result: any) => {
          this.foundParcelShops = result?.parcelShops || [];
          this.loadingParcelShops = false;
        },
        error: () => {
          this.foundParcelShops = [];
          this.loadingParcelShops = false;
        },
      });

      return;
    }

    this.loadingParcelShops = false;
  }

  formattedCustomerName(item: any): string {
    if (item && item.name) {
      return `${item.name}`;
    }

    if (item) {
      return `${item}`;
    }

    return '';
  }

  checkClientAddress(): void {
    if (this.clientService.clientAddress) {
      this.shipment.senderAddress = this.clientService.clientAddress;
      this.shipment.senderAddress.name = this.clientService.defaultClientName;
      this.shipment.senderAddress.contactEmail = this.clientService.defaultClientEmail;
      this.shipment.senderAddress.contactPhone = this.clientService.defaultClientPhone;
      this.shipment.senderAddress.contactPerson = this.clientService.defaultClientContactPerson;
    }
  }

  onClearAddress(): void {
    if (this.shipment.bookingType !== 'PARCELSHOP') {
      this.addressForm.get('zipCode')?.enable();
      this.addressForm.get('city')?.enable();
      this.addressForm.get('countryCode')?.enable();
    }

    this.addressForm.patchValue({
      street: '',
      zipCode: '',
      city: '',
      countryCode: '',
    });
  }

  checkAddressForm(): void {
    this.addressForm.valueChanges.subscribe((val) => {
      this.shipment.note = val.note;
      this.shipment.parcelShopId = val.parcelShopId;

      if (val.externalCarrierServices !== undefined) {
        if (this.multiSelectServices) {
          this.shipment.externalCarrierServices = val.externalCarrierServices;
        } else {
          this.shipment.service = val.externalCarrierServices;
        }
      }

      if (val.name) {
        this.shipment.recipientAddress.name = val.name;
      }

      if (val.contactPerson) {
        if (val.contactPerson.name) {
          this.shipment.recipientAddress.contactPerson = val.contactPerson.name;
        } else {
          this.shipment.recipientAddress.contactPerson = val.contactPerson;
        }
      }

      if (val.contactPhone) {
        this.shipment.recipientAddress.contactPhone = val.contactPhone;
      }

      if (val.contactEmail) {
        this.shipment.recipientAddress.contactEmail = val.contactEmail;
      }

      if (val.street) {
        this.shipment.recipientAddress.street = val.street;
      }

      if (val.zipCode) {
        this.shipment.recipientAddress.zipCode = val.zipCode;
      }

      if (val.city) {
        this.shipment.recipientAddress.city = val.city;
      }

      if (val.countryCode) {
        this.shipment.recipientAddress.countryCode = val.countryCode;
      }

      if (val.saveAddress !== undefined) {
        this.shipment.recipientAddress.saveAddress = val.saveAddress;
      }
    });
  }

  removeAllServices(event: any): void {
    this.shipment.externalCarrierServices = null;
    this.shipment.service = null;

    this.addressForm.patchValue({
      externalCarrierServices: this.multiSelectServices ? [] : null,
    });

    this.addressForm.get('externalCarrierServices')?.markAsDirty();
    this.addressForm.get('externalCarrierServices')?.updateValueAndValidity();

    this.externalServiceChange(event);
  }

  isDev(): boolean {
    return isDevMode();
  }
}